import { Star, Trash } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useAddGroupMember,
  useGroupMembers,
  useMembers,
  useRemoveGroupMember,
  useSetGroupMemberRole,
} from "~/api/queries";
import type { Group } from "~/api/types";
import { Pill, Row, Rows } from "~/features/automation/components";
import { Select } from "~/features/settings/controls";
import { Avatar } from "~/foundation/components/Avatar";
import { Button } from "~/foundation/components/Button";
import { Dialog } from "~/foundation/components/Dialog";
import { Empty } from "~/foundation/components/Empty";
import { IconButton } from "~/foundation/components/IconButton";
import { Spinner } from "~/foundation/components/Spinner";

/** The real roster (§A.6.2) — `groups.list` embeds only the first 8 memberships per group. */
export function GroupMembersDialog({ group, onClose }: { group: Group; onClose: () => void }) {
  const { t } = useTranslation();
  const { data, isLoading } = useGroupMembers(group.id);
  const { data: members } = useMembers();
  const addMember = useAddGroupMember();
  const removeMember = useRemoveGroupMember();
  const setRole = useSetGroupMemberRole();
  const [userId, setUserId] = useState("");

  const existingIds = new Set((data?.memberships ?? []).map((m) => m.userId));
  const candidates = (members ?? []).filter((m) => !existingIds.has(m.id));

  return (
    <Dialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("Team members")}
      description={group.name}
      closeLabel={t("Close")}
      size="lg"
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-end gap-2">
          <Select
            label={t("Person")}
            fullWidth
            value={userId}
            onChange={(event) => setUserId(event.target.value)}
          >
            <option value="">{t("Choose someone")}</option>
            {candidates.map((member) => (
              <option key={member.id} value={member.id}>
                {member.name || member.email}
              </option>
            ))}
          </Select>
          <Button
            variant="neutral"
            disabled={!userId || addMember.isPending}
            onClick={() => {
              addMember.mutate({ id: group.id, userId });
              setUserId("");
            }}
          >
            {t("Add")}
          </Button>
        </div>

        {isLoading ? (
          <Spinner label={t("Loading")} />
        ) : !data?.memberships.length ? (
          <Empty message={t("No members yet")} />
        ) : (
          <Rows>
            {data.memberships.map((membership) => {
              const member = data.users.find((u) => u.id === membership.userId);
              return (
                <Row key={membership.userId}>
                  <Avatar name={member?.name ?? membership.userId} size={24} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-body text-text">
                      {member?.name ?? membership.userId}
                    </p>
                    <p className="truncate text-small text-text-tertiary">{member?.email}</p>
                  </div>
                  {membership.role === "lead" ? (
                    <Pill tone="accent">
                      <Star size={12} aria-hidden className="me-1 inline" />
                      {t("Lead")}
                    </Pill>
                  ) : null}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      setRole.mutate({
                        id: group.id,
                        userId: membership.userId,
                        role: membership.role === "lead" ? "member" : "lead",
                      })
                    }
                  >
                    {membership.role === "lead" ? t("Make member") : t("Make lead")}
                  </Button>
                  <IconButton
                    icon={Trash}
                    aria-label={t("Remove")}
                    onClick={() => removeMember.mutate({ id: group.id, userId: membership.userId })}
                  />
                </Row>
              );
            })}
          </Rows>
        )}
      </div>
    </Dialog>
  );
}
